import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Instagram, Globe, Briefcase, Users, Building2 } from 'lucide-react';
import { AnimatedCounter } from './AnimatedCounter';

const stats = [
  { icon: Globe, value: 11, suffix: '', label: 'Countries Active' },
  { icon: Briefcase, value: 30, suffix: '+', label: 'Years Experience' },
  { icon: Users, value: 50, suffix: 'K+', label: 'Followers' },
  { icon: Building2, value: 8, suffix: '+', label: 'Companies' },
];

export const HeroSection = () => {
  const { ref, inView } = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="hero-section" data-testid="hero-section">
      {/* Background Elements */}
      <div className="hero-bg-pattern" />
      <div className="hero-grid" />
      
      {/* Animated Africa Map */}
      <div className="africa-map-container">
        <svg viewBox="0 0 400 450" className="africa-map-svg">
          <defs>
            <linearGradient id="africaGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.3" />
              <stop offset="50%" stopColor="#007AFF" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#50C878" stopOpacity="0.3" />
            </linearGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          <motion.path
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 3, ease: "easeInOut" }}
            d="M200 20 L240 30 L280 50 L310 80 L330 120 L340 160 L350 200 L355 250 L350 300 L340 340 L320 370 L290 395 L260 410 L230 420 L200 425 L170 420 L140 410 L110 395 L80 370 L60 340 L50 300 L45 250 L50 200 L60 160 L70 120 L90 80 L120 50 L160 30 Z"
            fill="url(#africaGradient)"
            stroke="#D4AF37"
            strokeWidth="1"
            filter="url(#glow)"
          />
          {[
            { cx: 200, cy: 120 },
            { cx: 280, cy: 220 },
            { cx: 250, cy: 280 },
            { cx: 150, cy: 180 },
            { cx: 310, cy: 180 },
          ].map((dot, index) => (
            <motion.g key={index}>
              <motion.circle
                cx={dot.cx}
                cy={dot.cy}
                r="4"
                fill="#D4AF37"
                initial={{ scale: 0 }}
                animate={{ scale: [1, 1.5, 1] }}
                transition={{
                  delay: index * 0.3 + 1,
                  duration: 2,
                  repeat: Infinity,
                  repeatType: "loop"
                }}
              />
              <motion.circle
                cx={dot.cx}
                cy={dot.cy}
                r="12"
                fill="none"
                stroke="#D4AF37"
                strokeWidth="1"
                initial={{ scale: 0, opacity: 0.6 }}
                animate={{ scale: [1, 2], opacity: [0.6, 0] }}
                transition={{
                  delay: index * 0.3 + 1,
                  duration: 2,
                  repeat: Infinity,
                  repeatType: "loop"
                }}
              />
            </motion.g>
          ))}
        </svg>
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 text-left" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Main Headline - New copy */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            <span className="text-white">I've Spent 30 Years Building</span>
            <br />
            <span className="text-white">Businesses Across India & Africa.</span>
            <br />
            <span className="gradient-text gold-text-glow">Now I Help You Do The Same.</span>
          </h1>

          {/* Subheadline - Reworded */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-lg lg:text-xl text-white/70 max-w-2xl mb-8 leading-relaxed"
          >
            Engineer. Entrepreneur. Coach. Active across 11 African countries since 2001 — I bridge the gap between your business idea and real-world execution.
          </motion.p>

          {/* Trust Strip - Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.6 }}
            className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10 max-w-2xl"
          >
            {stats.map((stat, index) => (
              <div key={stat.label} className="text-center sm:text-left">
                <div className="text-2xl sm:text-3xl font-bold text-[#D4AF37]">
                  <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-xs sm:text-sm text-white/50">{stat.label}</div>
              </div>
            ))}
          </motion.div>

          {/* CTA Buttons - New structure */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="flex flex-wrap gap-4 mb-8"
          >
            <button
              data-testid="hero-cta-packages"
              onClick={() => scrollToSection('#packages')}
              className="btn-primary"
            >
              Explore My Packages
            </button>
            <a
              href="https://instagram.com/businesswisdombykirit"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-instagram"
              className="btn-secondary flex items-center gap-2"
            >
              <Instagram size={18} />
              Follow @businesswisdombykirit
            </a>
          </motion.div>

          {/* Instagram Badge - Moved up for credibility */}
          <motion.a
            href="https://instagram.com/businesswisdombykirit"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-instagram"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
            className="instagram-badge inline-flex"
          >
            <Instagram size={18} className="text-[#E4405F]" />
            <span className="font-semibold">50K+ Followers</span>
            <span className="text-white/50">Daily Business Wisdom</span>
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};
